import { useNavigateToCreatorStudio } from '@src/hooks/useNavigateToCreatorStudio';
import { populateVideoVersionWithResources } from '@src/services/VideoResourcesProvider';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getAvailableClipFormats } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ActionsWrapper, ActionWrapperRow } from '../../styles/mainStyle';
import CirclePlayIcon from '../../svgs/CirclePlayIcon';
import EllipsisIcon from '../../svgs/EllipsisIcon';
import PencilIcon from '../../svgs/PencilIcon';
import RemoveIcon from '../../svgs/RemoveIcon';
import ShareIcon from '../../svgs/ShareIcon';
import { getFormattedDurationForStoryVideo } from '../../utility/timeFormat';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { getThumbnailUrlAndAspectRatio } from '../../utility/videoClip';
import ClientReadyFlag from '../common/ClientReadyFlag';
import { EditIcon } from '../common/icons';
import Modal from '../common/Modal';
import SpinningLoading from '../SpinningLoading';
import CopyContentModal from '../stage/CopyContentModal';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import LastPublishedInfo from './LastPublishedInfo';
import SocialShareStatus from './SocialShareStatus';
import SocialStats from './SocialStats';

import type {
  OtherVideo,
  VideoClip,
  VideoLastPublish,
} from '../../types.ts/story';

type VideoContent = OtherVideo & { autoPlay?: boolean };
type Props = {
  video: VideoContent;
};

const getPublishedAspectRatio = (video: VideoClip) => {
  const videos = [video, ...(video.associatedVideos || [])];
  const published = videos.find(v => v.videoFilePrimary);
  if (published) return published.aspectRatio;
  return video.aspectRatio;
};

const VideoClipCard = observer(({ video }: Props) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();

  const [openRenameDropdown, toggleRenameDropdown] = useState<boolean>(false);
  const [contentToEdit, toggleContentToEditModal] = useState<{
    type: 'rename';
  } | null>(null);
  const [loadingSpinnerText, setLoadingSpinnerText] = useState<
    string | undefined
  >();

  const renameRef = useRef<HTMLDivElement>(null);
  const { enableVideoClipEdit, enableSharing } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const navigate = useNavigateToCreatorStudio();

  useOutsideAlerter(renameRef, () => {
    toggleRenameDropdown(false);
  });

  const duration = getFormattedDurationForStoryVideo(video);
  const currentStory = videoCreator.story;
  const clips = [video, ...(video.associatedVideos || [])];
  const shareableClip = clips.find(clip => clip.shareableImageId);

  const { thumbnailUrl, aspectRatio } = getThumbnailUrlAndAspectRatio(
    video,
    shareableClip,
    currentStory,
    shareableClip?.aspectRatio || video.aspectRatio,
  );

  const shouldFillImage = () => {
    if (!shareableClip) return true;
    return !!video.shareableImageId;
  };
  const fillImage = shouldFillImage();

  async function loadVideoModal(autoPlay = false) {
    videoCreator.videoClipPreview = {
      ...video,
      selectedDimension: getPublishedAspectRatio(video),
    };
    const previewClip: VideoClip & {
      autoPlay?: boolean;
      selectedDimension?: string;
    } = await populateVideoVersionWithResources(
      currentStory!.id,
      video,
      ['videoSourceSnapshot'],
      true,
      'latest',
    );
    if (!video.videoFilePrimary && video.associatedVideos?.length) {
      const nonMainVideo = video.associatedVideos.find(v => v.videoFilePrimary);
      if (nonMainVideo) {
        previewClip.selectedDimension = nonMainVideo.aspectRatio;
      }
    }
    if (previewClip.id === video.id) {
      videoCreator.setClipPreviewAndUpdateSource({ ...previewClip, autoPlay });
    }
  }

  const renderPublishInfo = () => {
    const allPublishInfo = [
      video.lastPublishJson,
      ...(video.associatedVideos?.map(v => v.lastPublishJson) || []),
    ].filter(Boolean) as VideoLastPublish[];
    return <LastPublishedInfo lastPublishInfo={allPublishInfo} />;
  };

  const dimensions = getAvailableClipFormats(video);

  return (
    <Container>
      <Card>
        <VideoBadgeRow>
          <DurationBadge>{duration.formattedDuration}</DurationBadge>
          <DimensionsContainer>
            {dimensions.map(format => (
              <DimensionBadge
                inactive={!format.isPublished}
                key={format.aspectRatio}
              >
                {format.aspectRatio}
              </DimensionBadge>
            ))}
          </DimensionsContainer>
          {enableSharing && (
            <SocialIconsWrapper>
              <SocialShareStatus
                hideItems={
                  dimensions.length > 2 ? { x: true, twitter: true } : undefined
                }
                sharedContents={video._allReferencingSharedContents}
                padding="0"
              />
            </SocialIconsWrapper>
          )}
          <Kebab
            ref={renameRef}
            onClick={() => toggleRenameDropdown(!openRenameDropdown)}
          >
            <EllipsisIcon />
            <ActionsWrapper isVisible={openRenameDropdown}>
              <RenameRow
                role="button"
                onClick={() => toggleContentToEditModal({ type: 'rename' })}
              >
                <span>Rename</span> <PencilIcon strokeColor="currentColor" />
              </RenameRow>
              <RemoveRow
                role="button"
                onClick={async () => {
                  setLoadingSpinnerText('Removing video');
                  await videoCreator.hideVideo(video.id);
                  setLoadingSpinnerText(undefined);
                }}
              >
                <span>Remove Clip</span>{' '}
                <RemoveIcon
                  width={'17'}
                  height={'18'}
                  strokeColor="currentColor"
                />
              </RemoveRow>
            </ActionsWrapper>
          </Kebab>
        </VideoBadgeRow>

        <TitleContainer>
          <Title title={video.title}>{video.title}</Title>
        </TitleContainer>

        <ThumbnailWrapper onClick={() => loadVideoModal(true)}>
          <Thumbnail
            src={thumbnailUrl}
            alt="thumbnail"
            fill={fillImage}
            objectFit={aspectRatio === '9:16' ? 'contain' : 'cover'}
          />
          <div className="hover-overlay" />
          <HoverPlayButton className="play-icon" type="button">
            <CirclePlayIcon />
          </HoverPlayButton>
          {enableSharing && (
            <StatsOverlay>
              <SocialStats
                type="video"
                id={video.id}
                allReferencingSharedContents={
                  video._allReferencingSharedContents
                }
                layout="wide"
              />
            </StatsOverlay>
          )}
        </ThumbnailWrapper>

        <Buttons>
          {enableVideoClipEdit && (
            <EditButton
              onClick={() => {
                const activeVideo = video.videoFilePrimary
                  ? video
                  : video.associatedVideos?.find(v => v.videoFilePrimary) ||
                    video;
                navigate(activeVideo);
              }}
            >
              <EditIcon strokeColor="#F3E9D7" />
              Edit
            </EditButton>
          )}

          <ShareButton onClick={() => loadVideoModal(false)}>
            <ShareIcon /> Preview
          </ShareButton>
        </Buttons>

        <BottomRow>
          {renderPublishInfo()}
          <ClientReadyFlag iconOnly video={video} />
        </BottomRow>
      </Card>

      {contentToEdit && (
        <Modal
          isOpen={contentToEdit !== null}
          closeModal={() => toggleContentToEditModal(null)}
        >
          <CopyContentModal
            name={video.title! ?? ''}
            tag="Story"
            type={contentToEdit.type}
            onCancel={() => toggleContentToEditModal(null)}
            onSave={async title => {
              if (contentToEdit?.type === 'rename') {
                toggleContentToEditModal(null);
                setLoadingSpinnerText('Renaming video');
                await videoCreator.renameVideo(video.id!, title, false);
                setLoadingSpinnerText(undefined);
              }
            }}
          />
        </Modal>
      )}

      {loadingSpinnerText && (
        <SpinningLoading
          customStyle={{
            top: 0,
            position: 'fixed',
            alignItems: 'center',
          }}
          text={loadingSpinnerText}
        />
      )}
    </Container>
  );
});

export default VideoClipCard;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 392px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 24px;
  border-radius: 10px;
  border: 1px solid #484848;
  position: relative;

  &:hover,
  &:focus-within {
    .social-stats {
      opacity: 0;
    }
    .actions-menu {
      opacity: 1;
    }
  }
`;

const VideoBadgeRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 8px;
`;

const DimensionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DimensionBadge = styled.div<{ inactive?: boolean }>`
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  font-size: 10px;
  font-weight: 500;
  ${props =>
    props.inactive
      ? css`
          border-color: #484848;
          color: #484848;
        `
      : css`
          border-color: #17c964;
          color: #17c964;
        `}
`;

const SocialIconsWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const Kebab = styled.div`
  margin-left: 2px;
  cursor: pointer;
  height: 24px;
  display: flex;
  align-items: center;
  svg circle {
    transition: 0.2s ease;
  }
  &:hover {
    svg circle {
      fill: #f3e9d7;
    }
  }
`;

const RenameRow = styled(ActionWrapperRow)`
  display: flex;
  gap: 25px;
  justify-content: space-between;
`;

const RemoveRow = styled(RenameRow)`
  &:hover {
    background-color: #ef5d6f;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  height: 187px;
  display: flex;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 80px;
    background: linear-gradient(180deg, rgba(3, 4, 26, 0), rgba(3, 4, 26, 0.8));
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    pointer-events: none;
  }

  .hover-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.2);
    transition: 0.2s ease;
  }

  &:hover .hover-overlay {
    background: rgba(0, 0, 0, 0);
  }
  &:hover .play-icon {
    display: block;
    cursor: pointer;
  }
`;

const Thumbnail = styled.img<{
  fill: boolean;
  objectFit: 'cover' | 'contain';
}>`
  width: ${props => (props.fill ? '100%' : 'auto')};
  height: 187px;
  object-fit: ${props => props.objectFit};
  border-radius: 10px;
`;

const DurationBadge = styled.div`
  padding: 6px 8px;
  background-color: #f388bf;
  color: #03041a;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 120%;
`;

const HoverPlayButton = styled.button`
  position: absolute;
  top: 40%;
  left: 40%;
  display: none;
  background-color: transparent;
  outline: none;
  border: none;
`;

const StatsOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  z-index: 1;
  padding: 0 16px;
  pointer-events: none;
  > div {
    margin: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid #f3e9d7;
  background: none;
  cursor: pointer;
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
`;

const ShareButton = styled(EditButton)`
  background: #f3e9d7;
  color: black;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  > div:first-child {
    flex: 1;
  }
`;
